/**
 * Old Mobile QC display helpers for admin (mirrors customer site logic).
 */
import { IMEI_QC_KEYS, UNIT_QC_LABELS } from '../constants/oldMobileQc';

export const OLD_MOBILE_PHYSICAL_SPEC_KEYS = new Set([
  'conditionGrade',
  'batteryHealth',
  'ageMonths',
  'purchaseYear',
  'screenCondition',
  'scratchesLevel',
  'warrantyMonths',
  'refurbished',
  'hasBox',
  'hasBill',
  'hasCharger',
  'hasEarphones'
]);

const FUNCTIONAL_QC_ORDER = IMEI_QC_KEYS.map(({ key }) => key);

export function normalizeQcObject(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

function defaultPassFunctionalQc() {
  const o = {};
  for (const key of FUNCTIONAL_QC_ORDER) o[key] = true;
  return o;
}

function applyQcLayer(target, layer) {
  const o = normalizeQcObject(layer);
  if (!o) return;
  for (const [k, v] of Object.entries(o)) {
    if (OLD_MOBILE_PHYSICAL_SPEC_KEYS.has(k) || k === 'conditionNote') continue;
    if (v !== null && v !== undefined && v !== '') target[k] = v;
  }
}

/** Unit + listing merge; missing keys default to pass (customer site behaviour). */
export function effectiveFunctionalQcForUnit(unitQc, listingQc) {
  const unit = normalizeQcObject(unitQc);
  const listing = normalizeQcObject(listingQc);
  const hasUnit = unit && Object.keys(unit).some((k) => {
    if (k === 'conditionNote' || OLD_MOBILE_PHYSICAL_SPEC_KEYS.has(k)) return false;
    const v = unit[k];
    return v !== null && v !== undefined && v !== '';
  });
  const hasListing = listing && Object.keys(listing).some((k) => {
    if (OLD_MOBILE_PHYSICAL_SPEC_KEYS.has(k)) return false;
    const v = listing[k];
    return v !== null && v !== undefined && v !== '';
  });
  if (!hasUnit && !hasListing) return {};

  const merged = defaultPassFunctionalQc();
  applyQcLayer(merged, listing);
  applyQcLayer(merged, unit);
  return merged;
}

export function sortedFunctionalQcEntries(qcObject) {
  const o = normalizeQcObject(qcObject) || {};
  const seen = new Set();
  const out = [];
  for (const key of FUNCTIONAL_QC_ORDER) {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      const v = o[key];
      if (v !== null && v !== undefined && v !== '') {
        out.push([key, v]);
        seen.add(key);
      }
    }
  }
  for (const key of Object.keys(o)) {
    if (!seen.has(key) && !OLD_MOBILE_PHYSICAL_SPEC_KEYS.has(key) && key !== 'conditionNote') {
      const v = o[key];
      if (v !== null && v !== undefined && v !== '') out.push([key, v]);
    }
  }
  return out;
}

export function formatFunctionalQcLabel(key) {
  return UNIT_QC_LABELS[key] || String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function physicalSpecEntriesForQc(specs) {
  if (!specs || typeof specs !== 'object') return [];
  return Object.entries(specs).filter(([key, value]) => {
    if (!OLD_MOBILE_PHYSICAL_SPEC_KEYS.has(key)) return false;
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'boolean' && !value) return false;
    return true;
  });
}

const PHYSICAL_LABELS = {
  conditionGrade: 'Condition grade',
  batteryHealth: 'Battery health',
  ageMonths: 'Age',
  purchaseYear: 'Purchase year',
  screenCondition: 'Screen condition',
  scratchesLevel: 'Scratches / body wear',
  warrantyMonths: 'Warranty',
  refurbished: 'Refurbished',
  hasBox: 'Original box',
  hasBill: 'Bill / invoice',
  hasCharger: 'Charger',
  hasEarphones: 'Earphones'
};

export function formatPhysicalSpecLabel(key) {
  return PHYSICAL_LABELS[key] || formatFunctionalQcLabel(key);
}

export function formatPhysicalSpecValue(key, val) {
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  const str = String(val);
  if (key === 'batteryHealth') return `${str}%`;
  if (key === 'ageMonths' || key === 'warrantyMonths') return `${str} months`;
  return str;
}

export function isQcValuePassing(value) {
  return value === true || value === 'working' || value === 'ok';
}

export function isQcValueIssue(value) {
  return (
    value === false ||
    value === 'not working' ||
    value === 'issue' ||
    (typeof value === 'string' &&
      value &&
      value !== 'working' &&
      value !== 'ok' &&
      value !== 'issue')
  );
}
